import { useEffect, useState, useRef } from "react";
import { getEntry } from "lib/api";
import useIsMobile from "hooks/useIsMobile";
import { EntryType } from "lib/models";
import Entry from "components/Entry";
import { useAppContext } from "lib/state/TextProvider";
import SideBar from "components/SideBar/index";
import { FaSpinner } from "react-icons/fa";
import { urlHashToArray, arrayToUrlHash } from "lib/url";
import { useNavigate, useParams } from "react-router-dom";
import HomeDesktop from "./HomeDesktop";
import HomeMobile from "./HomeMobile";

const Home = () => {
  const { state, setEntry, setWorkspaceNavigation } = useAppContext();
  const { entryType, entryUuid, refsPath } = useParams<{
    entryType: EntryType;
    entryUuid: string;
    refsPath?: string;
  }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const hasLoadedEntry = useRef(false);

  const isMobile = useIsMobile();

  useEffect(() => {
    const loadEntry = async () => {
      if (hasLoadedEntry.current) return;
      if (entryUuid && entryType) {
        const entry = await getEntry(entryUuid, entryType);
        setEntry(entry);
        setIsLoading(false);
        hasLoadedEntry.current = true;
      }
    };
    loadEntry();

    return () => {
      hasLoadedEntry.current = false;
    };
  }, [entryType, entryUuid]);

  useEffect(() => {
    const loadNavigationPaths = async () => {
      if (refsPath) {
        try {
          const decodedPaths = urlHashToArray(refsPath);
          setWorkspaceNavigation(decodedPaths);
        } catch (error) {
          navigate(`/${entryType}/${entryUuid}`, { replace: true });
        }
      } else {
        setWorkspaceNavigation([]);
      }
    };
    loadNavigationPaths();
  }, [refsPath]);

  useEffect(() => {
    if (!state.workspaceNavigation.length) {
      return;
    }
    const latest =
      state.workspaceNavigation[state.workspaceNavigation.length - 1];
    navigate(
      `/${entryType}/${latest.uuid}/refs/${arrayToUrlHash(state.workspaceNavigation)}`,
      { replace: true },
    );
  }, [state.workspaceNavigation]);

  return isMobile ? (
    <HomeMobile
      isLoading={isLoading}
      model={state.entry}
      manifestation={state.userProfile?.manifestation}
    />
  ) : (
    <HomeDesktop
      isLoading={isLoading}
      model={state.entry}
      manifestation={state.userProfile?.manifestation}
    />
  );
};

export default Home;
